import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/Toast';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';

export function ResetPassword() {
  const navigate = useNavigate();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sessionReady, setSessionReady] = useState<boolean | null>(null);

  // Process recovery tokens from URL hash
  useEffect(() => {
    const processTokens = async () => {
      const hash = window.location.hash;
      
      if (hash && hash.includes('access_token')) {
        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        
        if (accessToken && refreshToken) {
          // Clear hash from URL
          window.history.replaceState(null, '', window.location.pathname);
          
          // Set the session
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (error) {
            console.error('Error setting session:', error);
            setSessionReady(false);
          } else if (data.session) {
            setSessionReady(true);
          } else {
            setSessionReady(false);
          }
          return;
        }
      }
      
      // No tokens in URL - check if we already have a session
      const { data: { session } } = await supabase.auth.getSession();
      // Reached directly while logged out, so there's no reset link to be invalid
      if (!session) {
        navigate('/login', { replace: true });
        return;
      }
      setSessionReady(true);
    };
    
    processTokens();
  }, []);

  // Determine loading state
  const isLoading = sessionReady === null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast.error(error.message);
      setLoading(false);
    } else {
      // Sign out after password reset so user can login fresh
      await supabase.auth.signOut();
      toast.success('Password updated successfully');
      navigate('/login', { replace: true });
    }
  };

  // Show loading while checking session
  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="main-wrapper _100vh">
          <Navbar />
          <div className="section-auth">
            <div className="container-medium z-index-2">
              <div className="loading-screen" style={{ minHeight: '200px' }}>
                <div className="loading-spinner" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no valid session
  if (!sessionReady) {
    return (
      <div className="page-wrapper">
        <div className="main-wrapper _100vh">
          <Navbar />
          <div className="section-auth">
            <div className="container-medium z-index-2">
              <div className="text-align-center">
                <h1 className="text-size-huge">Invalid or Expired Link</h1>
                <div className="spacer-tiny"></div>
                <div className="text-size-regular text-colour-alt-grey">
                  This password reset link is invalid or has expired.
                </div>
                <div className="spacer-large"></div>
                <button 
                  className="button is-wide w-button"
                  onClick={() => navigate('/forgot-password')}
                >
                  Request a new link
                </button>
              </div>
            </div>
          </div>
          <div className="spacer-medium"></div>
          <div className="absolute-footer-wrapper">
            <Footer />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="main-wrapper _100vh">
        <Navbar />
        <div className="section-auth">
          <div className="container-medium z-index-2">
            <div className="text-align-center">
              <h1 className="text-size-huge">Set new password</h1>
              <div className="spacer-tiny"></div>
              <div className="text-size-regular text-colour-alt-grey">
                Enter your new password below
              </div>
            </div>

            <div className="spacer-large"></div>

            <div className="auth-block">
              <div className="account-block w-form">
                <form onSubmit={handleSubmit}>
            {/* New Password Field */}
            <label htmlFor="password" className="form_label">
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                className="form_input is-light w-input"
                maxLength={256}
                name="password"
                placeholder="Enter new password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.6,
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>

            <div className="spacer-xsmall"></div>

            {/* Confirm Password Field */}
            <label htmlFor="confirmPassword" className="form_label">
              Confirm Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                className="form_input is-light w-input"
                maxLength={256}
                name="confirmPassword"
                placeholder="Confirm new password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.6,
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>

            <div className="spacer-small"></div>

            {/* Submit Button */}
            <input
              type="submit"
              data-wait="Please wait..."
              className="button is-wide is-large w-button"
              value={loading ? 'Updating...' : 'Update password'}
              disabled={loading}
            />
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="spacer-medium"></div>
          <div className="absolute-footer-wrapper">
            <Footer />
          </div>
        </div>
      </div>
  );
}
